import { Dialog, Router } from '@serenity-is/corelib';

// Key stamped into history.state when the page first loads.  The grid-title
// back-button loop stops here so it can never escape past the page that
// originated this navigation session.
const PAGE_ORIGIN_KEY = '__swm_nav_origin__';

// Pages where a back button on the grid title makes no sense
const EXCLUDED_GRID_PATHS = new Set([
    '/',
    '/shawverwell/customers',
    '/shawverwell/searchwellspumps',
]);

const EXCLUDED_GRID_PREFIXES = [
    '/administration/',
];

function isExcludedGridPage(): boolean {
    const raw = window.location.pathname;
    const path = (raw.length > 1 ? raw.replace(/\/$/, '') : raw).toLowerCase();
    if (EXCLUDED_GRID_PATHS.has(path)) return true;
    for (const prefix of EXCLUDED_GRID_PREFIXES) {
        if (path.startsWith(prefix)) return true;
    }
    return false;
}

// Returns true when the given hash fragment is a Serenity dialog route that
// should be skipped rather than navigated to.  This covers:
//   - Auto-generated shebang hashes: "!1", "!2a", …
//   - Nested-dialog separators: "edit/5/+/!1"
//   - Named entity routes that open dialogs: "new", "edit/123", "type/edit/123"
function isDialogHash(hash: string): boolean {
    return hash !== '' && (
        hash.includes('!') ||
        hash.includes('/+/') ||
        Router.mightBeRouteRegex.test(hash)
    );
}

// Guard: only one skip-loop in flight at a time (prevents double-clicks from
// multiplying history.back() calls and over-shooting the target).
let backNavigating = false;

// ── Grid-title back button ────────────────────────────────────────────────────
// Walks history.back() until we land on a non-dialog hash, the page-origin
// marker, or a cross-page navigation unloads this page.  Used on standalone
// grid pages (Wells, PumpWorks, …) where the intent is "go to the previous
// full page."
function goBackSkippingDialogs(): void {
    if (backNavigating) return;
    backNavigating = true;
    Router.enabled = false;
    let steps = 0;

    // Safety valve: if hashchange never fires within 500 ms (we hit the end of
    // history, or a cross-page navigation unloaded us) re-enable the Router.
    let fallback = setTimeout(() => {
        backNavigating = false;
        Router.enabled = true;
    }, 500);

    function onHashChange() {
        clearTimeout(fallback);
        steps++;
        const hash = window.location.hash.replace(/^#/, '');
        // Stop at the page-origin marker even if the hash still looks like a
        // dialog route (handles deep-linked pages that loaded with a hash).
        const atOrigin = !!(history.state as any)?.[PAGE_ORIGIN_KEY];

        if (!atOrigin && isDialogHash(hash) && steps < 10) {
            // Still on a dialog hash — keep going back.
            fallback = setTimeout(() => { backNavigating = false; Router.enabled = true; }, 500);
            window.addEventListener('hashchange', onHashChange, { once: true });
            history.back();
        } else {
            // Clean hash, origin entry, or step limit — re-enable and sync.
            backNavigating = false;
            Router.enabled = true;
            Router.resolve();
        }
    }

    window.addEventListener('hashchange', onHashChange, { once: true });
    history.back();
}

// ── Dialog-panel back button ──────────────────────────────────────────────────
// Closes the current panel dialog so that Serenity's normal onDialogClose
// chain takes over (e.g. CustomersSiteDialog re-opens CustomersDialog).
// This mirrors exactly what clicking the panel's own × button does, except
// we find the dialog from the title-bar element rather than a close button.
function closeCurrentPanel(titleEl: HTMLElement): void {
    // Dialog.getInstance's getDialogContentNode walks up to .s-Panel then
    // queries down for .panel-body — no need to navigate manually.
    Dialog.getInstance(titleEl)?.close();
}

function insertBackButton(titleEl: HTMLElement, isPanel: boolean): void {
    if (titleEl.querySelector('.back-btn')) return;

    const btn = document.createElement('a');
    btn.className = 'back-btn';
    btn.href = 'javascript:void(0)';
    btn.title = 'Go Back';

    const icon = document.createElement('i');
    icon.className = 'fa fa-arrow-left';
    btn.appendChild(icon);

    btn.addEventListener('click', isPanel
        ? () => closeCurrentPanel(titleEl)
        : goBackSkippingDialogs);
    titleEl.insertBefore(btn, titleEl.firstChild);
}

export function initBackButtonHandler(): void {
    // Stamp the current history entry as the page origin so the grid-title
    // back-button loop never navigates past this point.
    const existingState = history.state ?? {};
    if (!existingState[PAGE_ORIGIN_KEY]) {
        history.replaceState({ ...existingState, [PAGE_ORIGIN_KEY]: true }, '');
    }

    // Watch for both .grid-title (EntityGrid pages) and .panel-titlebar-text
    // (responsive/maximized EntityDialog panels) being added to the DOM.
    const observer = new MutationObserver(() => {
        if (!isExcludedGridPage()) {
            const gridTitle = document.querySelector<HTMLElement>('.grid-title');
            if (gridTitle) insertBackButton(gridTitle, false);
        }
        document.querySelectorAll<HTMLElement>('.panel-titlebar-text').forEach(el => {
            insertBackButton(el, true);
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Handle anything already in the DOM at call time
    if (!isExcludedGridPage()) {
        const gridTitle = document.querySelector<HTMLElement>('.grid-title');
        if (gridTitle) insertBackButton(gridTitle, false);
    }
    document.querySelectorAll<HTMLElement>('.panel-titlebar-text').forEach(el => {
        insertBackButton(el, true);
    });
}
